import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, CheckCircle, MapPin, Phone } from 'lucide-react';
import PageHero from '../components/PageHero';
import { Img } from '../components/Img';
import { IMG } from '../images';
import { AREAS, getArea } from '../areas';
import { FAQS } from '../faqs';

const SERVICE_LINKS = [
  { to: '/services/window-cleaning', label: 'High Rise Window & Facade Cleaning' },
  { to: '/services/remedial', label: 'Remedial Work & Building Maintenance' },
  { to: '/services/glazing', label: 'Glazing & Panel Installation' },
  { to: '/services/height-safety', label: 'Height Safety Systems' },
  { to: '/services/bird-control', label: 'Bird Pest Control' },
  { to: '/services/emergency', label: 'Emergency Rope Access' },
];

const Area = () => {
  const { areaSlug } = useParams();
  const area = getArea(areaSlug);

  if (!area) {
    return (
      <section className="section" style={{ marginTop: 'var(--header-h)' }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <h1 style={{ color: 'var(--navy-blue)', marginBottom: '1rem' }}>Area not found</h1>
          <p style={{ marginBottom: '2rem' }}>We could not find that service area.</p>
          <Link to="/areas" className="btn-primary">See all service areas</Link>
        </div>
      </section>
    );
  }

  const heroImage = area.image === 'facade' ? IMG.facade : IMG.hero;
  const others = AREAS.filter((a) => a.slug !== area.slug);

  return (
    <div>
      <PageHero image={heroImage} position="55% 45%" title={area.headline} subtitle="IRATA Level 3 certified technicians. Fast response. Clear, tailored quotes." minHeight="56vh">
        <div className="hero-buttons">
          <Link to="/contact#quote-form" className="btn-primary">Request a Quote <ArrowRight size={18} aria-hidden="true" /></Link>
          <a href="tel:0426920301" className="btn-secondary"><Phone size={18} aria-hidden="true" /> 0426 920 301</a>
        </div>
      </PageHero>

      <section className="section">
        <div className="split">
          <div className="reveal">
            <span className="eyebrow">{area.name}</span>
            <h2>Safe, Efficient Work at Height in {area.name}</h2>
            {area.intro.map((p) => (
              <p key={p}>{p}</p>
            ))}
            <h3 style={{ color: 'var(--navy-blue)', fontSize: '1.5rem', margin: '1.5rem 0 0.5rem' }}>What we do here</h3>
            <ul className="check-list">
              {area.focus.map((f) => (
                <li key={f}><CheckCircle size={20} aria-hidden="true" /> {f}</li>
              ))}
            </ul>
            <Link to="/contact#quote-form" className="btn-primary">Get a Quote for {area.name}</Link>
          </div>
          <div className="photo-frame reveal d1">
            <Img image={IMG.tower} alt={IMG.tower.alt} sizes="(max-width: 900px) 100vw, 520px" style={{ aspectRatio: '3 / 4', maxHeight: '600px' }} />
          </div>
        </div>
      </section>

      <section className="section bg-light">
        <div className="container">
          <div className="section-header reveal">
            <h2>Areas We Cover Around {area.name}</h2>
            <p>We regularly work in these suburbs and nearby areas.</p>
          </div>
          <ul style={{ listStyle: 'none', display: 'flex', flexWrap: 'wrap', gap: '0.75rem', justifyContent: 'center', maxWidth: '900px', margin: '0 auto' }}>
            {area.suburbs.map((s) => (
              <li key={s} style={{ background: 'var(--white)', border: '1px solid var(--border-color)', padding: '8px 16px', display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 600, color: 'var(--navy-blue)' }}>
                <MapPin size={16} style={{ color: 'var(--safety-orange)' }} aria-hidden="true" /> {s}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="section-header reveal">
            <h2>Our Services in {area.name}</h2>
          </div>
          <div className="mini-links">
            {SERVICE_LINKS.map((s) => (
              <Link key={s.to} to={s.to} className="mini-link">
                <span>{s.label}</span>
                <ArrowRight size={18} aria-hidden="true" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="section bg-light">
        <div className="container">
          <div className="section-header reveal">
            <h2>Common Questions</h2>
          </div>
          <div className="faq">
            {FAQS.slice(0, 4).map((item) => (
              <details key={item.q}>
                <summary>{item.q}</summary>
                <p>{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="section bg-dark">
        <div className="container" style={{ textAlign: 'center', color: 'var(--white)' }}>
          <h2 style={{ color: 'var(--white)', marginBottom: '1.25rem', fontSize: 'clamp(2rem, 4vw, 3rem)' }}>Other Areas We Service</h2>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', justifyContent: 'center', marginBottom: '2rem' }}>
            {others.map((a) => (
              <Link key={a.slug} to={`/areas/${a.slug}`} className="btn-secondary" style={{ padding: '10px 18px', fontSize: '0.95rem' }}>{a.name}</Link>
            ))}
          </div>
          <a href="tel:0426920301" className="btn-primary"><Phone size={18} aria-hidden="true" /> Call 0426 920 301</a>
        </div>
      </section>
    </div>
  );
};

export default Area;
